/**
 * English translations
 * TypeScript file for typing and autocompletion
 */

export const en = {
  common: {
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    close: "Close",
    confirm: "Confirm",
    loading: "Loading...",
    error: "Error",
    success: "Success",
    yes: "Yes",
    no: "No",
    back: "Back",
    next: "Next",
    previous: "Previous",
    search: "Search",
    submit: "Submit"
  },
  nav: {
    home: "Home",
    courses: "Courses",
    webinars: "Webinars",
    about: "About",
    faq: "FAQ",
    contact: "Contact",
    aySolutions: "AY Solutions",
    login: "Login",
    signup: "Sign Up",
    logout: "Logout",
    profile: "My Profile",
    admin: "Admin",
    dashboard: "Dashboard",
    menu: "Menu"
  },
  auth: {
    email: "Email",
    password: "Password",
    confirmPassword: "Confirm Password",
    forgotPassword: "Forgot Password?",
    rememberMe: "Remember Me",
    alreadyHaveAccount: "Already have an account?",
    dontHaveAccount: "Don't have an account?",
    resetPassword: "Reset Password",
    verifyEmail: "Verify Email"
  },
  login: {
    studentsImageAlt: "AY Solutions Academy Students",
    connectToAccessCourse: "Log in to access the course",
    createAccountOrLogin: "Create an account or log in to start learning",
    welcomeBack: "Welcome back",
    pleaseLogin: "Please log in to your account",
    emailPlaceholder: "Email",
    passwordPlaceholder: "Password",
    forgotPassword: "Forgot Password?",
    connecting: "Connecting...",
    login: "Log in",
    noAccountYet: "Don't have an account yet?",
    signup: "Sign up",
    forgotPasswordTitle: "Forgot Password?",
    enterEmailForReset: "Enter your email to receive a reset link",
    yourEmail: "Your email",
    cancel: "Cancel",
    sending: "Sending...",
    send: "Send",
    enterEmailAddress: "Please enter your email address",
    resetEmailSent: "Reset email sent to {{email}}",
    emailSendFailed: "Failed to send email",
    loginError: "Login error",
    free: "Free",
    paidCourse: "Paid Course"
  },
  signup: {
    studentsImageAlt: "AY Solutions Academy Students",
    createAccountToAccessCourse: "Create an account to access the course",
    signupNowToStart: "Sign up now to start learning",
    welcomeToAY: "Welcome to AY",
    signup: "Sign up",
    fullName: "Full Name",
    emailPlaceholder: "Email",
    passwordPlaceholder: "Password",
    confirmPasswordPlaceholder: "Confirm Password",
    phoneOptional: "Phone (optional)",
    addressOptional: "Address (optional)",
    cityOptional: "City (optional)",
    countryOptional: "Country (optional)",
    signingUp: "Signing up...",
    signupButton: "Sign Up",
    alreadyHaveAccount: "Already have an account?",
    login: "Log in",
    signupError: "Signup error",
    free: "Free",
    paidCourse: "Paid Course"
  },
  course: {
    title: "Title",
    description: "Description",
    content: "Content",
    enroll: "Enroll",
    start: "Start",
    continue: "Continue",
    progress: "Progress",
    modules: "Modules",
    lessons: "Lessons",
    quiz: "Quiz",
    assignment: "Assignment",
    download: "Download"
  },
  admin: {
    dashboard: "Dashboard",
    users: "Users",
    courses: "Courses",
    payments: "Payments",
    settings: "Settings"
  },
  home: {
    marqueeDiscount: "20% off on all courses until December 31st!",
    marqueeCourses: "Over 500 courses available",
    marqueeSatisfaction: "98% customer satisfaction",
    marqueeCertificates: "Certificates recognized by employers",
    marqueeLearn: "Learn from anywhere",
    marqueePractical: "Practical and up-to-date training",
    marqueeInstructors: "👨‍🏫 Expert instructors",
    marqueeLifetime: "🔄 Lifetime access to courses",
    newLearningPlatform: "🎓 New learning platform",
    learnSkills: "Learn the skills of",
    tomorrow: "tomorrow",
    heroDescription: "Discover quality online courses created by experts. Develop your skills at your own pace, wherever you are.",
    searchPlaceholder: "Search for a course...",
    exploreCourses: "Explore courses",
    qualityCourses: "Quality courses",
    certificatesIncluded: "Certificates included",
    atYourPace: "At your own pace",
    studentsOnline: "Online students",
    howItWorks: "How it works?",
    howItWorksSubtitle: "Start your learning journey in 3 simple steps",
    step1Title: "Choose your course",
    step1Description: "Browse our catalog and select the course that interests you",
    step2Title: "Learn at your own pace",
    step2Description: "Follow video lessons, do exercises and progress step by step",
    step3Title: "Get your certificate",
    step3Description: "Validate your skills and receive your certificate of completion",
    whyChooseUs: "Why choose AY Solutions Academy?",
    whyChooseUsSubtitle: "A platform designed for your success",
    coursesAvailable: "Courses available",
    available24h: "24/7",
    onlineStudent: "Online training student",
    recognizedCertifications: "Recognized certifications",
    recognizedCertificationsDesc: "At the end of each training, you receive a certificate attesting to your skills, valuable on your CV or LinkedIn.",
    totalFlexibility: "Total flexibility",
    totalFlexibilityDesc: "Learn where you want and when you want, without schedule constraints.",
    qualifiedTrainers: "Qualified trainers",
    qualifiedTrainersDesc: "All our courses are led by experts and professionals, with clear pedagogy adapted to all levels.",
    interactiveLearning: "Interactive and up-to-date learning",
    interactiveLearningDesc: "Regularly updated courses, with quizzes, practical exercises and case studies to immediately apply your knowledge.",
    discoverPrograms: "Discover our programs",
    discoverProgramsSubtitle: "An overview of our training catalog",
    viewAllCourses: "View all courses",
    loadingCourses: "Loading courses...",
    noPopularCourses: "No popular courses available",
    noPopularCoursesDesc: "Courses will be available soon.",
    recommendedForYou: "Our training programs",
    recommendedForYouSubtitle: "Courses selected according to your interests",
    noRecommendedCourses: "No recommended courses available",
    noRecommendedCoursesDesc: "Courses will be available soon.",
    testimonials: "Testimonials",
    testimonialsSubtitle: "Discover what our students say about their experience",
    testimonial1Name: "Marie Kouassi",
    testimonial1Role: "Web Developer",
    testimonial1Text: "AY Solutions Academy transformed my career. The courses are well structured and the instructors are excellent. I was able to land a developer role thanks to the skills I gained here.",
    testimonial2Name: "Jean Traoré",
    testimonial2Role: "Data Analyst",
    testimonial2Text: "An outstanding platform! The flexibility to learn at my own pace and the quality of the content helped me progress quickly. I highly recommend it.",
    testimonial3Name: "Aminata Diallo",
    testimonial3Role: "Project Manager",
    testimonial3Text: "The certificates earned on AY Solutions Academy are recognized by my employer. The practical learning and real-world cases have helped me a lot in my day-to-day work."
  },
  about: {
    title: "About AY Solutions Academy",
    subtitle: "We are revolutionizing online education in Africa by making learning accessible, engaging and adapted to the realities of our continent.",
    ourStory: "Our Story",
    ourStoryText1: "AY Solutions Academy was founded in 2026 by AY SOLUTIONS, leveraging its recognized expertise in digital innovation and consulting.",
    ourStoryText2: "The platform was born from a strategic conviction: using technology to democratize quality education by breaking down geographical constraints.",
    ourStoryText3: "Since its launch, our evolution has been marked by a focus on user experience and pedagogical excellence, aiming to empower students and effectively prepare them for tomorrow's job market challenges.",
    ourVision: "Our Vision",
    ourVisionText1: "To become the African reference for online education, measuring our success by the direct and real impact on our learners' career progression.",
    ourVisionText2: "We work for a dynamic, intelligent and highly personalized learning ecosystem, strongly powered by Artificial Intelligence (AI). This approach aims to redefine e-learning, offering pedagogical richness comparable to face-to-face learning while maintaining total flexibility.",
    statsTitle: "Our Results in Numbers",
    statsSubtitle: "The impact of our platform on education in Africa",
    activeStudents: "Active Students",
    availableCourses: "Available Courses",
    expertTrainers: "Expert Trainers",
    satisfactionRate: "Satisfaction Rate",
    goals2025: "Our Goals for 2026",
    goalsSubtitle: "Concrete commitments to grow our community and strengthen the impact of our training.",
    goal1Title: "1. Catalog Expansion",
    goal1Desc: "Integrate at least 20 new certified programs, with a particular focus on Cybersecurity and Data Science, to anticipate market needs.",
    goal2Title: "2. Community Growth",
    goal2Desc: "Reach 100,000 active learners on the platform to consolidate our regional leadership and foster peer exchanges.",
    goal3Title: "3. Technological Improvement",
    goal3Desc: "Launch the V2 mobile application to offer a complete and smooth experience, including offline learning and smart notifications.",
    goal4Title: "4. Learner Success",
    goal4Desc: "Maintain an 80% completion rate by strengthening personalized tutoring, career coaching sessions and support resources.",
    goal5Title: "5. Recognition",
    goal5Desc: "Obtain international quality certification to validate our pedagogical methodologies and guarantee a constant level of requirement.",
    valuesTitle: "Our Values",
    valuesSubtitle: "The principles that guide our mission and shape every aspect of our platform",
    academicExcellence: "Academic Excellence",
    academicExcellenceDesc: "We are committed to providing the highest quality education, with rigorously selected and regularly updated content.",
    accessibilityForAll: "Accessibility for All",
    accessibilityForAllDesc: "Education must be accessible to everyone, regardless of location, time or financial means. That's why we offer free and affordable courses.",
    techInnovation: "Technological Innovation",
    techInnovationDesc: "We use the latest technologies to create interactive and engaging learning experiences that adapt to your needs.",
    practicalLearning: "Practical Learning",
    practicalLearningDesc: "Our courses are designed to be immediately applied in the real world, with concrete projects and practical use cases.",
    teamTitle: "Our Team",
    teamSubtitle: "Passionate professionals working every day to improve your learning experience",
    joinMission: "Join Our Mission",
    joinMissionText: "Together, let's build the future of education in Africa. Your learning journey starts here.",
    discoverCourses: "Discover Our Courses",
    contactUs: "Contact Us"
  },
  contact: {
    title: "Contact Us",
    subtitle: "We are here to help. Feel free to contact us for any questions or assistance.",
    email: "Email",
    sendEmail: "Send us an email",
    phone: "Phone",
    address: "Address",
    hours: "Hours",
    mondayToFriday: "Monday to Friday",
    sendMessage: "Send us a message",
    sendMessageDesc: "Fill out the form below and we will respond quickly.",
    fullName: "Full Name",
    fullNamePlaceholder: "Your name",
    emailPlaceholder: "your@email.com",
    subject: "Subject",
    subjectPlaceholder: "Subject of your message",
    message: "Message",
    messagePlaceholder: "Describe your request in detail...",
    sending: "Sending...",
    sendButton: "Send Message",
    messageSent: "Message sent successfully! We will respond as soon as possible.",
    quickSolutions: "Quick Solutions",
    quickSolutionsDesc: "Need immediate help? Try these options.",
    liveChat: "Live Chat",
    liveChatDesc: "Get an instant response",
    startChat: "Start Chat",
    techSupport: "Technical Support",
    techSupportDesc: "Help with your courses",
    contactSupport: "Contact Support",
    faq: "FAQ",
    faqDesc: "Frequently Asked Questions",
    viewFAQ: "View FAQ",
    frequentQuestions: "Frequently Asked Questions",
    faqSubtitle: "Quickly find answers to your questions.",
    faq1Question: "How can I recover my password?",
    faq1Answer: "Use the \"Forgot Password\" link on the login page.",
    faq2Question: "How do I access my courses?",
    faq2Answer: "Log in and go to the \"My Courses\" section of your profile.",
    faq3Question: "Technical problem with a video?",
    faq3Answer: "Check your internet connection and refresh the page.",
    viewAllFAQ: "View All FAQ",
    ourLocation: "Our Location",
    locationSubtitle: "Come visit us at our offices",
    interactiveMap: "Interactive Map"
  },
  courses: {
    title: "Discover Our Programs",
    subtitle: "Over 50 courses created by experts to develop your skills",
    searchPlaceholder: "Search for a course, a category...",
    all: "All",
    loading: "Loading courses...",
    errorTitle: "Loading Error",
    errorMessage: "An error occurred",
    coursesAvailable: "courses available",
    coursesIn: "courses in",
    sortBy: "Sort by",
    popularity: "Popular",
    recent: "Recent",
    oldest: "Oldest",
    noCoursesFound: "No courses found",
    noCoursesFoundDesc: "Try modifying your search or filter criteria."
  },
  courseDetail: {
    loading: "Loading course...",
    errorTitle: "Loading error",
    errorMessage: "An error occurred",
    backToCourses: "Back to courses",
    courseNotFound: "Course not found",
    courseNotFoundDesc: "The requested course does not exist or is no longer available.",
    category: "Category",
    beginner: "Beginner",
    intermediate: "Intermediate",
    advanced: "Advanced",
    reviews: "reviews",
    students: "students",
    lessons: "lessons",
    instructor: "Instructor",
    description: "Description",
    myProgress: "My progress",
    completed: "Completed",
    inProgress: "In progress",
    globalProgress: "Overall progress",
    completedLessons: "Completed lessons",
    completedModules: "Completed modules",
    lastVisit: "Last visit:",
    enrolledOn: "Enrolled on:",
    moduleDetails: "Module details",
    modules: "modules",
    noModulesAvailable: "No modules available for this course.",
    progression: "Progress:",
    blocks: "blocks",
    lessonsTitle: "Lessons",
    startModule: "Start module",
    continueModule: "Continue module",
    takeQuiz: "Take quiz",
    retakeQuiz: "Retake quiz",
    retakeQuizButton: "Retake quiz",
    enrollInCourse: "Enroll in course",
    startCourse: "Start course",
    continueCourse: "Continue course",
    accessExpired: "Access expired",
    accessExpiredDesc: "Your access period to this course has expired",
    expiredOn: "Expired on",
    renewAccess: "Renew access",
    certificateIssued: "Certificate issued",
    certificateIssuedDesc: "Congratulations! You have completed this course and received your certificate.",
    viewCertificate: "View certificate",
    coursePrice: "Course price",
    free: "Free",
    enrollNow: "Enroll now",
    mustBeEnrolled: "You must be enrolled in the course to start",
    enrollmentError: "Error starting course",
    enrollmentSuccess: "Enrollment successful!",
    youMustBeEnrolled: "You must be enrolled in the course to start",
    congratulations: "Congratulations!",
    certificateObtained: "You have obtained your certificate",
    viewMyCertificate: "View my certificate",
    yourProgress: "Your progress",
    lessonsCompleted: "lessons completed out of",
    starting: "Starting...",
    lifetimeAccess: "Lifetime access",
    completionCertificate: "Completion certificate",
    youAreEnrolled: "You are enrolled in this course",
    accessUntil: "Access until",
    courseCompleted: "Course completed successfully",
    completedOn: "Completed on",
    enrollmentInfo: "Enrollment information",
    status: "Status",
    enrolled: "Enrolled",
    completedStatus: "Completed",
    enrolledOnDate: "Enrolled on:",
    courseInfo: "Course Information",
    availableLanguages: "Available languages: ",
    lastUpdate: "Last update: ",
    certificateIncluded: "Certificate included",
    loginToAccess: "Log in to access this course",
    createAccountOrLogin: "Create an account or log in to start learning",
    fullCourseAccess: "Full access to course content",
    trackProgress: "Track your progress",
    createAccount: "Create account",
    alreadyHaveAccount: "I already have an account",
    termsAndConditions: "By continuing, you agree to our terms of use",
    completedOnDate: "Completed on: ",
    expiredOnDate: "Expires on: "
  },
  courseReader: {
    mustValidatePreviousQuiz: "You must validate the previous module's quiz to access this module",
    moduleUnlocked: "Module \"{{title}}\" unlocked! You can access it now.",
    mustValidateModuleQuiz: "You must validate the quiz of module \"{{title}}\" before you can take this quiz.",
    mustCompletePreviousModules: "You must complete all previous modules before you can take this quiz.",
    contentNotAvailable: "Content not available for this block.",
    loading: "Loading...",
    unsupportedContentType: "Unsupported content type: {{type}}",
    loadingContent: "Loading content...",
    errorTitle: "Loading error",
    errorMessage: "An error occurred",
    backToCourse: "Back to course",
    moduleLocked: "Module locked",
    moduleLockedDesc: "This module is locked",
    goToPreviousModule: "Go to previous module",
    noContentAvailable: "No content available",
    noContentDesc: "This module does not contain any content yet.",
    moduleContent: "Module content",
    moduleOf: "Module {{current}} of {{total}}",
    courseModules: "Course modules",
    passed: "passed",
    score: "Score: {{score}}%",
    locked: "Locked",
    moduleProgress: "Module progress",
    quiz: "Quiz",
    passedQuiz: "✓ Passed",
    bestScore: "Best: {{score}}%",
    notAttempted: "Not attempted",
    available: "Available",
    attempts: "attempt",
    attemptsPlural: "attempts",
    canRetake: "• Can retake",
    mustCompleteAllBlocks: "You must complete all blocks in the module before you can take the quiz.",
    completePreviousModules: "Complete all previous modules to access the quiz",
    completeAllBlocks: "Complete all blocks to access the quiz",
    viewQuiz: "View quiz",
    retakeQuiz: "Retake quiz",
    takeQuiz: "Take quiz",
    quizLocked: "Quiz locked",
    previous: "Previous",
    markAsCompleted: "Mark as completed",
    loadingNav: "Loading...",
    goToQuiz: "Go to quiz",
    nextModule: "Next module",
    finish: "Finish",
    next: "Next"
  },
  quiz: {
    certificateMissing: "Certificate missing",
    certificateIdMissing: "Certificate ID missing",
    invalidBlob: "Response is not a valid Blob",
    invalidFile: "Response is not a valid file",
    emptyFile: "Downloaded file is empty",
    downloadSuccess: "Certificate downloaded successfully",
    downloadError: "Error downloading certificate",
    attemptCancelled: "Your quiz attempt has been cancelled. You can start again.",
    leavingWarning: "You are taking a quiz. If you leave now, your attempt will be cancelled and you will lose an attempt.",
    leavingConfirm: "You are taking a quiz. If you leave now, your attempt will be cancelled and you will lose an attempt. Do you really want to leave?",
    noAnswer: "No answer",
    quizTitle: "Quiz",
    quizOfModule: "Module Quiz",
    moduleLocked: "Module locked",
    moduleLockedDesc: "This module is locked",
    goToPreviousModule: "Go to previous module",
    backToCourse: "Back to course",
    quizAlreadyPassed: "Quiz already passed",
    quizAlreadyPassedDesc: "You have already successfully passed this quiz. You cannot retake it.",
    bestScore: "Best score:",
    attemptsCount: "Number of attempts:",
    backToCourseButton: "Back to course",
    quizLocked: "Quiz locked",
    mustCompleteAllBlocks: "You must complete all blocks in the module before you can take the quiz.",
    mustValidateModuleQuiz: "You must validate the quiz of module \"{{title}}\" before you can take this quiz.",
    mustCompletePreviousModules: "You must complete all previous modules before you can take this quiz.",
    backToModule: "Back to module",
    loadingQuiz: "Loading quiz...",
    errorTitle: "Loading error",
    errorMessage: "An error occurred while loading the quiz.",
    noQuizAvailable: "No quiz available",
    noQuizDesc: "This module does not have an associated quiz.",
    questionOf: "Question {{current}} of {{total}}",
    questionNumber: "Question {{number}}",
    questions: "Questions",
    minutes: "Minutes",
    requiredScore: "Required score: {{score}}%",
    maxAttempts: "Max attempts: {{attempts}}",
    instructions: "Instructions",
    startQuiz: "Start quiz",
    requiredQuestion: "Required question",
    previous: "Previous",
    submitting: "Submitting...",
    finishQuiz: "Finish quiz",
    next: "Next",
    submittingTitle: "Submitting...",
    submittingDesc: "Please wait while we evaluate your answers.",
    results: "Results",
    maxAttemptsReached: "You have reached the maximum number of attempts allowed.",
    congratulations: "Congratulations! You passed the quiz.",
    scoreNotReached: "You did not reach the required score.",
    quizNotSubmitted: "Quiz not submitted:",
    attemptsExhausted: "❌ Attempts exhausted",
    yourScore: "Your score:",
    passed: "Passed",
    failed: "❌ Failed",
    requiredScoreLabel: "Required score: {{score}}%",
    timeSpent: "Time spent",
    attempt: "Attempt",
    remaining: "Remaining",
    pointsObtained: "Points obtained",
    correctAnswers: "Correct answers",
    errors: "Errors",
    congratulationsCertificate: "Congratulations! Certificate obtained!",
    completionCertificate: "Completion Certificate",
    courseCompleted: "Course completed",
    certificateNumber: "Certificate number:",
    issuedOn: "Issued on",
    viewMyCertificates: "View my certificates",
    downloadPDF: "Download PDF",
    certificateProgress: "Progress towards certificate",
    eligibleForCertificate: "You are eligible for the certificate!",
    completeAllModules: "Complete all modules to get your certificate.",
    completedModules: "Completed modules: {{completed}} / {{total}}",
    remainingModules: "Remaining modules: {{count}}",
    modulesRemaining: "You have {{count}} module(s) remaining to complete to get your certificate.",
    quizzesToRetake: "Quizzes to retake: {{count}}",
    mustRetakeQuizzes: "You must retake some quizzes to get your certificate.",
    attemptsExhaustedTitle: "Attempts exhausted",
    messages: "Messages",
    cannotRetake: "You can no longer retake this quiz. Contact your instructor if needed.",
    reviewAnswers: "Review answers",
    reviewDesc: "Details of your answers question by question",
    yourAnswer: "Your answer:",
    type: "Type:",
    timeSpentLabel: "Time spent:",
    submissionError: "Submission error",
    submissionErrorDesc: "An error occurred during submission. The displayed score is calculated locally.",
    continueToNextModule: "Continue to next module: {{title}}",
    continueCourse: "Continue course",
    retakeQuiz: "Retake quiz ({{remaining}} attempts remaining)",
    retakeQuizUnlimited: "Return to course content",
    maxAttemptsReachedTitle: "Maximum number of attempts reached",
    allAttemptsUsed: "You have used all your attempts ({{max}}) for this quiz.",
    contactInstructor: "Contact your instructor if you need help.",
    congratulationsTitle: "Congratulations!",
    courseCompletedWithCertificate: "You have completed the course and obtained your certificate!",
    allModulesCompleted: "You have completed all modules of the course!",
    writeAnswerHere: "Write your answer here...",
    manuallyEvaluated: "This question will be manually evaluated by the instructor.",
    unsupportedQuestionType: "Unsupported question type",
    courseCompletedMessage: "Congratulations! You have completed the course and obtained your certificate!",
    allModulesCompletedMessage: "Congratulations! You have completed all modules of the course!",
    excellentWork: "Excellent work! Go to the next module: \"{{title}}\"",
    quizValidated: "Quiz validated! The next module is now accessible.",
    maxAttemptsError: "Maximum number of attempts reached",
    quizOfModuleTitle: "Quiz - {{title}}",
    returnToCoursesList: "Return to the courses list to see your progress.",
    ongoingAttempt: "An attempt is in progress",
    resumeQuiz: "Resume quiz",
    startedAt: "Started on",
    evaluateCourse: "Evaluate the course"
  },
  courseReview: {
    title: "Evaluate the course",
    courseTitle: "Course:",
    ratingLabel: "Rating (optional)",
    commentLabel: "Comment (optional)",
    commentPlaceholder: "Share your experience with this course...",
    commentHint: "Your comment will be visible to other students",
    requiredMessage: "You must evaluate this course before you can download your certificate.",
    ratingOrCommentRequired: "Please provide a rating or leave a comment",
    commentTooLong: "Comment cannot exceed 2000 characters",
    reviewSubmitted: "Your review has been successfully submitted!",
    submitError: "An error occurred while submitting your review",
    cancel: "Cancel",
    submit: "Submit review",
    submitting: "Submitting..."
  },
  certificate: {
    certificate: "Certificate",
    certified: "Certified",
    preview: "Preview",
    download: "Download",
    certificatePreview: "Certificate preview",
    completionCertificate: "Completion certificate",
    certificateNumber: "Certificate number",
    issuedOn: "Issued on",
    certificateIdMissing: "Certificate ID missing",
    invalidFile: "Invalid file",
    emptyFile: "Empty file",
    downloadSuccess: "Download started successfully",
    downloadError: "An error occurred during download",
    evaluationRequired: "You must evaluate the course before downloading the certificate"
  },
  profile: {
    loadingProfile: "Loading profile...",
    profile: "Profile",
    password: "Password",
    courses: "My courses",
    achievements: "Certificates",
    notifications: "Notifications",
    settings: "Settings",
    administration: "Administration",
    student: "Student",
    administrator: "Administrator",
    enrolledCourses: "enrolled courses",
    adminAccess: "Admin access",
    cancel: "Cancel",
    saving: "Saving...",
    save: "Save",
    edit: "Edit",
    logout: "Logout",
    personalInformation: "Personal Information",
    fullName: "Full Name",
    email: "Email",
    phone: "Phone",
    address: "Address",
    city: "City",
    country: "Country",
    passwordManagement: "Password Management",
    currentPassword: "Current Password",
    enterCurrentPassword: "Enter your current password",
    newPassword: "New Password",
    enterNewPassword: "Enter your new password",
    confirmNewPassword: "Confirm New Password",
    confirmNewPasswordPlaceholder: "Confirm your new password",
    updatePassword: "Update Password",
    updating: "Updating...",
    passwordSecurity: "Password Security",
    passwordSecurityDesc1: "Use at least 6 characters",
    passwordSecurityDesc2: "Mix letters, numbers and symbols",
    passwordSecurityDesc3: "Avoid personal information",
    learningStats: "Learning Statistics",
    enrolledCoursesCount: "Enrolled Courses",
    inProgressCourses: "In Progress Courses",
    completedCourses: "Completed Courses",
    myCourses: "My Courses",
    myCertificates: "My Certificates",
    loadingCertificates: "Loading certificates...",
    certified: "Certified",
    issuedOn: "Issued on",
    downloadCertificate: "Download Certificate",
    noCertificates: "No certificates",
    noCertificatesDesc: "Complete courses to get your certificates!",
    discoverCourses: "Discover Courses",
    adminDashboard: "Dashboard",
    adminDashboardDesc: "Overview",
    adminUsers: "Users",
    adminUsersDesc: "User management",
    adminCourses: "Courses",
    adminCoursesDesc: "Course management",
    adminAccessDesc: "Access platform administration tools.",
    notificationPreferences: "Notification Preferences",
    emailNotifications: "Email Notifications",
    emailNotificationsDesc: "Receive updates by email",
    courseUpdates: "Course Updates",
    courseUpdatesDesc: "Notifications for new content",
    newCourses: "New Courses",
    newCoursesDesc: "Discover new available courses",
    studyReminders: "Study Reminders",
    studyRemindersDesc: "Reminders to continue your courses",
    certificates: "Certificates",
    certificatesDesc: "Notifications for your certificates",
    generalSettings: "General Settings",
    language: "Language",
    timezone: "Timezone",
    theme: "Theme",
    light: "Light",
    dark: "Dark",
    auto: "Auto",
    profileUpdated: "Profile updated successfully",
    profileUpdateError: "Error updating profile",
    passwordMismatch: "Passwords do not match",
    passwordTooShort: "Password must contain at least 6 characters",
    passwordUpdated: "Password updated successfully",
    passwordUpdateError: "Error updating password",
    certificateDownloadSuccess: "Certificate downloaded successfully",
    certificateDownloadError: "Error downloading certificate",
    certificateIdMissing: "Certificate ID missing",
    invalidFile: "Response is not a valid file"
  },
  verifyEmail: {
    verifying: "Verifying",
    pleaseWait: "Please wait while we verify your email...",
    emailVerified: "Email Verified!",
    emailVerifiedDesc: "Your email has been verified successfully. You can get started.",
    startNow: "Start Now",
    verificationError: "Verification Error",
    noToken: "No verification token found. The link may be invalid or expired.",
    verificationFailed: "Unable to verify your email. The link may be invalid or expired.",
    backToLogin: "Back to Login",
    retry: "Retry",
    emailVerifiedSuccess: "Email verified successfully",
    emailVerificationError: "Email verification error"
  },
  resetPassword: {
    title: "Reset Password",
    invalidLink: "Invalid or expired link. Please request a new reset link.",
    resetting: "Resetting...",
    resetSuccess: "Password reset successfully. Redirecting...",
    resetError: "Password reset failed",
    newPasswordPlaceholder: "New Password",
    confirmPasswordPlaceholder: "Confirm Password",
    resetButton: "Reset",
    resettingButton: "Resetting...",
    resetSuccessMessage: "Password reset successfully"
  },
  modals: {
    emailVerification: {
      title: "Verify Your Email",
      message: "We have sent a verification link to:",
      clickLink: "Click the link in the email to activate your account.",
      openMailbox: "Open My Mailbox",
      resendEmail: "Resend Email",
      sending: "Sending...",
      emailResent: "Email Resent!",
      emailResentSuccess: "Verification email resent!",
      important: "Important:",
      importantNote: "You must verify your email before you can log in."
    },
    coursePayment: {
      title: "Course Access",
      whatYouGet: "What you get:",
      fullAccess: "Full access to course content",
      completionCertificate: "Completion certificate",
      communitySupport: "Community support",
      freeUpdates: "Free updates",
      lifetimeAccess: "Lifetime access • Certificate included",
      securePayment: "Secure Payment",
      securePaymentDesc: "Your payment is protected by SSL encryption and processed securely.",
      generatingLink: "Generating Payment Link",
      generatingLinkDesc: "Please wait while we prepare your payment...",
      proceedToPayment: "Proceed to Payment",
      processing: "Processing...",
      instantPayment: "Instant Payment • Immediate Access",
      courseInfoMissing: "Course information missing",
      paymentLinkError: "Error generating payment link",
      paymentError: "Error processing payment",
      redirectingToPayment: "Redirecting to payment..."
    },
    contentBlock: {
      addBlock: "Add Block",
      editBlock: "Edit Block",
      blockTitle: "Block Title",
      blockTitlePlaceholder: "Block Title",
      textContent: "Text Content",
      textContentPlaceholder: "Enter the text content of your block...",
      uploadImage: "Upload Image",
      imageUploadDesc: "Drag and drop an image or click to select a file",
      videoUrl: "Video URL",
      videoUrlPlaceholder: "https://youtube.com/watch?v=... or https://vimeo.com/...",
      videoUrlDesc: "Supports YouTube, Vimeo and other video platforms",
      assignmentInstructions: "Assignment Instructions",
      assignmentPlaceholder: "Describe the assignment instructions, objectives, evaluation criteria...",
      assignmentDesc: "You can add hints and expected output examples later",
      downloadInstructions: "Download Instructions",
      downloadPlaceholder: "Describe the files to download and instructions...",
      downloadDesc: "You can add files later via the administration interface",
      quizQuestions: "Quiz Questions",
      question: "Question",
      questionType: "Question Type *",
      multipleChoice: "Multiple Choice",
      singleChoice: "Single Choice",
      questionText: "Question *",
      questionPlaceholder: "What is your question?",
      points: "Points for this question *",
      pointsDesc: "Number of points awarded if the question is answered correctly",
      answerOptions: "Answer Options *",
      option: "Option",
      multipleChoiceDesc: "Check all correct answers. Partial points possible: if a question is worth X points with N correct answers, each correct answer is worth X/N points.",
      singleChoiceDesc: "Select the correct answer. All points will be awarded if the answer is correct, 0 otherwise.",
      addQuestion: "Add Question",
      cancel: "Cancel",
      save: "Save",
      add: "Add Block"
    },
    moduleLock: {
      title: "Module Locked",
      mustValidatePrevious: "You must validate the previous module's quiz \"{{previousTitle}}\" to access module \"{{lockedTitle}}\".",
      moduleLocked: "Module \"{{lockedTitle}}\" is locked.",
      previousModuleToComplete: "Previous module to complete:",
      quizValidated: "✓ Quiz validated",
      bestScore: "Best score: {{score}}% ({{status}})",
      quizNotStarted: "Quiz not started",
      close: "Close",
      goToPrevious: "Go to Previous Module",
      passed: "Passed",
      failed: "Failed"
    }
  },
  enrollmentGate: {
    connectToAccess: "Connect to access this course",
    createAccountOrLogin: "Create an account or log in to start learning",
    free: "Free",
    benefits: {
      fullAccess: "Full access to course content",
      trackProgress: "Track your progress",
      completionCertificate: "Completion certificate",
      lifetimeAccess: "Lifetime access"
    },
    createAccount: "Create Account",
    alreadyHaveAccount: "I already have an account",
    termsAcceptance: "By continuing, you agree to our terms of use",
    courseFree: "Free Course",
    later: "Later",
    enrollInCourse: "Enroll in Course",
    enrolling: "Enrolling...",
    enrollmentSuccess: "You are now enrolled in \"{{courseTitle}}\"!",
    enrollmentError: "Enrollment error. Please try again."
  },
  footer: {
    description: "Your new online learning platform.",
    emailPlaceholder: "Your email",
    courses: "Courses",
    webDevelopment: "Web Development",
    design: "Design",
    dataScience: "Data Science",
    entrepreneurship: "Entrepreneurship",
    platform: "Platform",
    about: "About",
    contact: "Contact",
    blog: "Blog",
    help: "Help",
    legal: "Legal",
    terms: "Terms",
    privacy: "Privacy",
    cookies: "Cookies",
    copyright: "© {{year}} AY Solutions Academy. All rights reserved."
  },
  comments: {
    title: "Comments",
    comments: "comments",
    writeComment: "Write your comment...",
    post: "Post",
    loginToComment: "Please log in to comment",
    loginRequired: "You must be logged in to perform this action",
    noComments: "No comments yet. Be the first to comment!",
    sortRecent: "Most recent",
    sortOldest: "Oldest",
    sortMostLiked: "Most liked",
    reply: "Reply",
    replies: "replies",
    writeReply: "Write your reply...",
    noReplies: "No replies",
    edited: "edited",
    pinned: "Pinned",
    confirmDelete: "Are you sure you want to delete this comment?",
    page: "Page",
    filters: "Filters",
    advancedFilters: "Advanced filters",
    clearFilters: "Clear filters",
    all: "All",
    filterReplies: "Replies",
    withReplies: "With replies",
    withoutReplies: "Without replies",
    filterPinned: "Pinned",
    pinnedOnly: "Pinned only",
    filterEdited: "Edited",
    editedOnly: "Edited only",
    minLikes: "Minimum likes",
    minLikesPlaceholder: "e.g. 5",
    filterAuthor: "Author",
    authorIdPlaceholder: "Author ID",
    sortRelevant: "Relevance"
  },
  webinars: {
    title: "Webinars",
    subtitle: "Discover our webinars and register",
    searchPlaceholder: "Search for a webinar...",
    filters: {
      all: "All",
      upcoming: "Upcoming",
      ongoing: "Ongoing",
      completed: "Completed"
    },
    status: {
      upcoming: "Upcoming",
      ongoing: "Ongoing",
      completed: "Completed",
      unknown: "Unknown"
    },
    registered: "Registered",
    draft: "Draft",
    full: "Full",
    notFound: "Webinar not found",
    notFoundDesc: "The requested webinar does not exist or is no longer available.",
    backToList: "Back to webinars",
    noWebinars: "No webinars available",
    loginPrompt: "Log in to register for webinars",
    pagination: "Page",
    details: {
      title: "Information",
      description: "Description",
      minutes: "minutes",
      registered: "registered",
      max: "max"
    },
    noDescription: "No description available.",
    register: "Register for webinar",
    registering: "Registering...",
    cancelRegistration: "Cancel registration",
    cancelling: "Cancelling...",
    loginToRegister: "Log in to register",
    joinMeeting: "Join meeting",
    watchReplay: "Watch replay",
    myWebinars: {
      title: "My Webinars",
      subtitle: "Manage your webinar registrations",
      upcoming: "Upcoming",
      completed: "Replays",
      noUpcoming: "No upcoming webinars",
      noCompleted: "No replays available",
      browseWebinars: "Browse webinars",
      instructions: {
        title: "Instructions",
        join: "Click 'Join meeting' to access the webinar",
        cancel: "You can cancel your registration at any time"
      },
      replays: {
        title: "Available replays",
        description: "You can rewatch webinars you were registered for"
      }
    }
  }
} as const

